import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import Modal, { btnSecondary } from "@/components/Modal";

const ConfirmContext = createContext(() => Promise.resolve(false));

export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options) => {
    setState({
      title: "Are you sure?",
      message: "",
      confirmLabel: "Confirm",
      cancelLabel: "Cancel",
      danger: true,
      ...options,
    });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (result) => {
    setState(null);
    resolver.current?.(result);
    resolver.current = null;
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <Modal title={state.title} onClose={() => close(false)} testid="confirm-dialog">
          <div className="space-y-5" data-testid="confirm-dialog-body">
            <div
              className={`flex items-start gap-3 rounded-lg px-4 py-3 border ${
                state.danger
                  ? "bg-[#FEF2F2] border-[#FECACA] text-[#B91C1C]"
                  : "bg-[#F9F3E6] border-[#E5C992] text-[#8A6D2F]"
              }`}
            >
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">{state.message}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => close(false)} className={btnSecondary} data-testid="confirm-cancel-button">
                {state.cancelLabel}
              </button>
              <button
                onClick={() => close(true)}
                data-testid="confirm-accept-button"
                className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors shadow-sm flex items-center gap-2 ${
                  state.danger
                    ? "bg-[#B91C1C] hover:bg-[#991B1B] text-white"
                    : "bg-[#C5A059] hover:bg-[#B08B46] text-[#2A1810] border border-[#B08B46]"
                }`}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}
